import isArray from 'lodash.isarray'
//https://github.com/elgerlambert/redux-localstorage
/**
 * @description
 * getSubset returns an object with the same structure as the original object passed in,
 * but contains only the specified keys and only if that key has a truth-y value.
 *
 * @param {Object} obj The object from which to create a subset.
 * @param {String[]} paths An array of (top-level) keys that should be included in the subset.
 *
 * @return {Object} An object that contains the specified keys with truth-y values
 */
function getSubset(obj, paths) {
  const subset = {}

  paths.forEach(key => {
    const slice = obj[key]
    if (slice) subset[key] = slice
  })

  return subset
}

/**
 * @description
 * Function that verifies if the persistedState exists
 * and if so overwrites initialState properties with new values
 *
 * @param {Object} initialState
 * @param {Object} persistedState
 * @returns {Object} Merged state
 */
function merge(initialState, persistedState) {
  return persistedState ? { ...initialState, ...persistedState } : initialState
}

/**
 * @description
 * slicer inspects the typeof paths and returns an appropriate slicer function.
 *
 * @param {String|String[]} [paths] The paths argument supplied to persistState.
 *
 * @return {Function} A slicer function, which returns the subset to store when called with Redux's store state.
 */
function slicer(paths) {
  switch (typeOf(paths)) {
    case 'void':
      return state => state
    case 'string':
      return state => getSubset(state, [paths])
    case 'array':
      return state => getSubset(state, paths)
    default:
      return console.error('Invalid paths argument, should be of type String, Array or Void')
  }
}

/**
 * @description
 * typeof method that
 * 1. groups all false-y & empty values as void
 * 2. distinguishes between object and array
 *
 * @param {*} thing The thing to inspect
 *
 * @return {String} Actionable type classification
 */
function typeOf(thing) {
  if (!thing) return 'void'

  if (isArray(thing)) {
    if (!thing.length) return 'void'
    return 'array'
  }

  return typeof thing
}

/**
 * @description
 * persistState is a Store Enhancer that syncs (a subset of) store state to localStorage.
 *
 * @param {String|String[]} [paths] Specify keys to sync with localStorage, if left undefined the whole store is persisted
 * @param {Object} [config] Optional config object
 * @param {String} [config.key="redux"] String used as localStorage key
 * @param {Function} [config.slicer] (paths) => (state) => subset. A function that returns a subset
 * of store state that should be persisted to localStorage
 * @param {Function} [config.serialize=JSON.stringify] (subset) => serializedData. Called just before persisting to
 * localStorage. Should transform the subset into a format that can be stored.
 * @param {Function} [config.deserialize=JSON.parse] (persistedData) => subset. Called directly after retrieving
 * persistedState from localStorage. Should transform the data into the format expected by your application
 *
 * @return {Function} An enhanced store
 */

export default function persistState(paths, key = 'redux') {
  const serialize = JSON.stringify
  const deserialize = JSON.parse

  return next => (reducer, initialState, enhancer) => {
    if (typeof initialState === 'function' && typeof enhancer === 'undefined') {
      enhancer = initialState
      initialState = undefined
    }

    let persistedState
    let finalInitialState

    try {
      persistedState = deserialize(localStorage.getItem(key))
      finalInitialState = merge(initialState, persistedState)
    } catch (e) {
      console.warn('Failed to retrieve initial state from localStorage:', e)
    }

    const store = next(reducer, finalInitialState, enhancer)
    const slicerFn = slicer(paths)

    store.subscribe(function() {
      const state = store.getState()
      const subset = slicerFn(state)

      try {
        localStorage.setItem(key, serialize(subset))
      } catch (e) {
        console.warn('Unable to persist state to localStorage:', e)
      }
    })

    return store
  }
}



// WEBPACK FOOTER //
// ./state/enhancers/persistState.js